import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { createSignedMediaUploadUrl } from "@/app/Media/GraphQL/mediaMutations";
import { Visibility } from "@/gql/graphql";

export type PendingAttachment = {
  id: string;
  filePath: string;
  file: File;
};

export type UploadingAttachment = {
  id: string;
  file: File;
};

export type MessageAttachments = ReturnType<typeof useMessageAttachments>;

export function useMessageAttachments() {
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [uploading, setUploading] = useState<UploadingAttachment[]>([]);
  const uploadMutation = useUploadAttachment();

  async function add(files: File[]) {
    if (files.length === 0) {
      return;
    }

    const entries = files.map((file) => ({ id: crypto.randomUUID(), file }));
    setUploading((prev) => [...prev, ...entries]);

    const results = await Promise.allSettled(
      entries.map((entry) => uploadMutation.mutateAsync(entry.file)),
    );

    results.forEach((result, index) => {
      const entry = entries[index];
      setUploading((prev) => prev.filter((f) => f.id !== entry.id));

      if (result.status === "fulfilled") {
        setPending((prev) => [...prev, result.value]);
      } else {
        toast.error(`Failed to upload ${entry.file.name}`);
      }
    });
  }

  function remove(id: string) {
    setPending((prev) => prev.filter((f) => f.id !== id));
  }

  function clear() {
    setPending([]);
  }

  return {
    pending,
    uploading,
    add,
    remove,
    clear,
    isUploading: uploading.length > 0,
    filePaths: pending.map((f) => f.filePath),
  };
}

function useUploadAttachment() {
  const { mutateAsync: getSignedUrl } = useGqlMutation(
    createSignedMediaUploadUrl,
  );

  return useMutation({
    mutationFn: async (file: File): Promise<PendingAttachment> => {
      const { createSignedStorageUrl } = await getSignedUrl({
        input: { visibility: Visibility.Private },
      });

      const uploadResponse = await fetch(createSignedStorageUrl.url, {
        method: "PUT",
        body: file,
        headers: {
          ...createSignedStorageUrl.headers,
          Host: undefined,
          "Content-Type": file.type,
        },
        credentials: "omit",
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      return {
        id: crypto.randomUUID(),
        filePath: createSignedStorageUrl.filePath,
        file,
      };
    },
  });
}
